import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, UserPlus } from 'lucide-react';
import { buttonClass } from '@/components/ui/Button';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { useFormato } from '@/hooks/useFormato';
import { useI18n } from '@/hooks/useI18n';
import { BarraDeSesion } from './identidad/BarraDeSesion';
import { useMensajeDeAcceso } from './identidad/useMensajeDeAcceso';
import { puertoDeDatos } from './datos/elPuertoDeDatos';
import type { AltaDeInversionista, Administracion } from './datos/puerto';
import { RUTA_PANEL } from './rutas';
import styles from './area.module.css';

/**
 * Quién tiene acceso al área, y darlo.
 *
 * Es la heredera de `PorteroInversionistas.tsx`, que se fue con el backend el
 * 2026-09-18 y que **no se recuperó del historial**: aquélla colgaba de Cognito
 * y de un plano de identidad que ya no existe. De ella se conserva la pregunta,
 * no el código.
 *
 * ## Esta pantalla no protege nada
 *
 * El enlace sólo se pinta si la sesión trae rol de administrador, y aun así
 * **eso es cortesía**: la ruta se alcanza escribiéndola, y con `curl` ni
 * siquiera hace falta el navegador. Lo que cierra la puerta es el 403 de
 * `capital`, comprobado en el servidor, con pruebas que cubren las dos rutas.
 *
 * Si alguien llega aquí sin el rol, verá el mensaje de `sinPermiso` y una lista
 * vacía. Eso es lo correcto: es exactamente lo que el servidor le contesta.
 *
 * ## La contraseña se enseña una vez y se dice que es una vez
 *
 * La sortea el servidor y no se vuelve a poder consultar --se guarda hasheada
 * con Argon2id--. Una pantalla que la enseñe sin avisar de eso produce a
 * alguien que cierra la pestaña y vuelve a buscarla.
 */
export function PorteroInversionistasPage() {
  const { t } = useI18n();
  const p = t.area.portero;
  const formato = useFormato();
  const mensaje = useMensajeDeAcceso();

  useDocumentMeta(t.meta.portero);

  const [datos, setDatos] = useState<Administracion | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<unknown>(null);

  const [correo, setCorreo] = useState('');
  const [nombre, setNombre] = useState('');
  const [organizacion, setOrganizacion] = useState('');
  const [invitando, setInvitando] = useState(false);
  const [reciente, setReciente] = useState<AltaDeInversionista | null>(null);
  const [errorDeAlta, setErrorDeAlta] = useState<unknown>(null);

  const cargar = useCallback(() => {
    setCargando(true);
    setError(null);
    puertoDeDatos
      .inversionistas()
      .then(setDatos)
      .catch(setError)
      .finally(() => setCargando(false));
  }, []);

  useEffect(cargar, [cargar]);

  const invitar = useCallback(
    (evento: React.FormEvent) => {
      evento.preventDefault();
      setInvitando(true);
      setErrorDeAlta(null);
      setReciente(null);
      puertoDeDatos
        .invitar({
          correo: correo.trim(),
          nombre: nombre.trim() || undefined,
          organizacion: organizacion.trim() || undefined,
        })
        .then((alta) => {
          setReciente(alta);
          setCorreo('');
          setNombre('');
          setOrganizacion('');
          // Se recarga la lista en vez de empujar la fila a mano: así lo que
          // se ve es lo que hay en la base, y no una version optimista que
          // puede diferir si el alta fue idempotente sobre un correo que ya
          // existia.
          cargar();
        })
        .catch(setErrorDeAlta)
        .finally(() => setInvitando(false));
    },
    [correo, nombre, organizacion, cargar],
  );

  return (
    <div className={styles.page}>
      <div className="ilu-container">
        <header className={styles.head}>
          <h1 className={styles.title}>{p.titulo}</h1>
          <p className={styles.subtitle}>{p.subtitulo}</p>
          <BarraDeSesion />
        </header>

        <p className={[styles.aviso, styles.avisoNeutro].join(' ')} role="status">
          <ShieldCheck size={16} aria-hidden="true" style={{ verticalAlign: '-3px' }} /> {p.aviso}
        </p>

        {/* --- Invitar ---------------------------------------------------- */}
        <div className={styles.card} style={{ marginBottom: 24 }}>
          <h2 className={styles.cardTitle}>
            <UserPlus size={16} aria-hidden="true" /> {p.invitar.titulo}
          </h2>
          <p className={styles.metricHint} style={{ marginBottom: 16 }}>
            {p.invitar.descripcion}
          </p>

          <form onSubmit={invitar}>
            <div className={styles.field}>
              <label htmlFor="portero-correo">{p.invitar.correo}</label>
              <input
                id="portero-correo"
                type="email"
              required
                autoComplete="off"
                value={correo}
                onChange={(e) => setCorreo(e.target.value)}
              />
            </div>

            <div className={styles.field}>
              <label htmlFor="portero-nombre">{p.invitar.nombre}</label>
              <input
                id="portero-nombre"
                type="text"
                autoComplete="off"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
              />
            </div>

            <div className={styles.field}>
              <label htmlFor="portero-organizacion">{p.invitar.organizacion}</label>
              <input
                id="portero-organizacion"
                type="text"
                autoComplete="off"
                value={organizacion}
                onChange={(e) => setOrganizacion(e.target.value)}
              />
            </div>

            {/*
              No hay campo de contraseña, y no es un olvido: la sortea el
              servidor. Un campo aquí invitaría a poner la misma para diez
              personas, y la haría viajar escrita dentro de la petición.
            */}
            <button
              type="submit"
              className={buttonClass({ variant: 'primary' })}
              disabled={invitando || correo.trim().length === 0}
              style={{ marginTop: 12 }}
            >
              {invitando ? p.invitar.enviando : p.invitar.cta}
            </button>
          </form>

          {errorDeAlta != null && (
            <p className={styles.vacio} role="alert" style={{ marginTop: 12 }}>
              {mensaje(errorDeAlta)}
            </p>
          )}

          {reciente && (
            <div className={styles.aviso} role="status" style={{ marginTop: 16 }}>
              <p>
                <strong>{p.invitar.creada(reciente.correo)}</strong>
              </p>
              <p className={styles.metricHint}>{p.invitar.unaSolaVez}</p>
              {/*
                En un `<code>` y seleccionable: quien la copia la va a pegar en
                un mensaje, y una contraseña sorteada de 20 caracteres escrita a
                mano se escribe mal.
              */}
              <code className={styles.codigo}>{reciente.contrasena}</code>
            </div>
          )}
        </div>

        {/* --- Quién tiene acceso ------------------------------------------ */}
        <h2 className={styles.cardTitle} style={{ marginTop: 32 }}>
          {p.lista.titulo}
        </h2>
        <div className={styles.card}>
          {cargando ? (
            <p className={styles.vacio} role="status">
              {t.area.comun.cargando}
            </p>
          ) : error != null ? (
            <>
              <p className={styles.vacio} role="alert">
                {mensaje(error)}
              </p>
              <button type="button" className={buttonClass({ variant: 'primary' })} onClick={cargar}>
                {t.area.comun.reintentar}
              </button>
            </>
          ) : !datos || datos.cuentas.length === 0 ? (
            <p className={styles.vacio}>{p.lista.vacia}</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className={styles.tabla}>
                <thead>
                  <tr>
                    <th scope="col">{p.lista.correo}</th>
                    <th scope="col">{p.lista.nombre}</th>
                    <th scope="col">{p.lista.rol}</th>
                    <th scope="col">{p.lista.alta}</th>
                    <th scope="col">{p.lista.ultimoIngreso}</th>
                    <th scope="col">{p.lista.estado}</th>
                  </tr>
                </thead>
                <tbody>
                  {datos.cuentas.map((c) => (
                    <tr key={c.id}>
                      <td>{c.correo}</td>
                      <td>{c.nombre ?? p.lista.sinNombre}</td>
                      <td>{c.rol}</td>
                      <td>{c.creadoEn ? formato.fechaDeCalendario(c.creadoEn.slice(0, 10)) : '—'}</td>
                      {/*
                        `nunca` y no un guion: que alguien no haya entrado NUNCA
                        es un dato, y de los que más dicen en una lista de
                        invitaciones. Un guion se lee como «no se sabe».
                      */}
                      <td>
                        {c.ultimoIngreso
                          ? formato.fechaDeCalendario(c.ultimoIngreso.slice(0, 10))
                          : p.lista.nunca}
                      </td>
                      <td>
                        {c.revocadoEn
                          ? p.lista.revocada
                          : c.debeCambiar
                            ? p.lista.sinEstrenar
                            : p.lista.activa}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* --- Lo que paso ------------------------------------------------ */}
        {datos && datos.sucesos.length > 0 && (
          <>
            <h2 className={styles.cardTitle} style={{ marginTop: 32 }}>
              {p.sucesos.titulo}
            </h2>
            <div className={styles.card}>
              {/*
                Esto es lo que hace honesta a la pantalla de recuperar
                contrasena. Alli el servidor contesta lo mismo exista o no la
                cuenta --es lo que impide enumerar correos-- asi que quien pide
                un codigo NO se entera de que SES lo rechazo. El fallo no se
                pierde: aparece aqui.

                Importa hoy mas que manana: las dos cuentas estan en el sandbox
                de SES, que entrega solo a direcciones verificadas, asi que
                ahora mismo el rechazo es el caso normal.
              */}
              <p className={styles.metricHint} style={{ marginBottom: 16 }}>
                {p.sucesos.explicacion}
              </p>
              <div style={{ overflowX: 'auto' }}>
                <table className={styles.tabla}>
                  <thead>
                    <tr>
                      <th scope="col">{p.sucesos.cuando}</th>
                      <th scope="col">{p.sucesos.que}</th>
                      <th scope="col">{p.sucesos.detalle}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {datos.sucesos.map((s, i) => (
                      <tr key={`${s.ocurridoEn}-${i}`}>
                        <td>
                          {s.ocurridoEn
                            ? formato.fechaDeCalendario(s.ocurridoEn.slice(0, 10))
                            : '—'}
                        </td>
                        <td>{s.suceso}</td>
                        <td>{s.detalle ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}

        <p style={{ marginTop: 24 }}>
          <Link to={RUTA_PANEL}>{p.volver}</Link>
        </p>
      </div>
    </div>
  );
}
